import { forwardRef, TextareaHTMLAttributes } from 'react'
import styled from 'styled-components'

export interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helperText?: string
  /** Shows a «typed/maxLength» readout; needs maxLength to render. */
  showCounter?: boolean
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  ({ label, error, helperText, showCounter, id, ...rest }, ref) => {
    const areaId = id || rest.name
    const typed = typeof rest.value === 'string' ? rest.value.length : 0
    return (
      <Field>
        {label && <Label htmlFor={areaId}>{label}</Label>}
        <Area id={areaId} ref={ref} $hasError={Boolean(error)} {...rest} />
        {(error || helperText || (showCounter && rest.maxLength)) && (
          <FootRow>
            <Helper $isError={Boolean(error)}>{error || helperText}</Helper>
            {showCounter && rest.maxLength && (
              <Counter>{`${typed}/${rest.maxLength}`}</Counter>
            )}
          </FootRow>
        )}
      </Field>
    )
  },
)
TextAreaField.displayName = 'TextAreaField'

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink2};
  white-space: nowrap;
`

const Area = styled.textarea<{ $hasError: boolean }>`
  width: 100%;
  min-height: 5rem;
  box-sizing: border-box;
  resize: vertical;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid
    ${({ theme, $hasError }) => ($hasError ? theme.colors.danger : theme.colors.line)};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 0.625rem 0.875rem;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.ink};
  outline: none;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast},
    box-shadow ${({ theme }) => theme.motion.fast};

  &::placeholder {
    color: ${({ theme }) => theme.colors.ink4};
  }

  &:focus {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.danger : theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.line2};
    cursor: not-allowed;
  }
`

const FootRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const Helper = styled.span<{ $isError: boolean }>`
  font-size: 0.75rem;
  color: ${({ theme, $isError }) => ($isError ? theme.colors.danger : theme.colors.ink3)};
`

const Counter = styled.span`
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.ink4};
`

export default TextAreaField
